import React, { useEffect, useRef, useState } from "react";
import { Form, ListGroup, Spinner } from "react-bootstrap";

// API adresse du gouvernement (Base Adresse Nationale) : gratuite, sans clé.
// Accepte aussi bien une saisie progressive qu'une adresse complète collée.
const API_URL = "https://api-adresse.data.gouv.fr/search/";

const parseFeature = (feature) => {
  const properties = feature.properties || {};
  let street = properties.street || properties.name || "";
  // Pour un résultat de type "housenumber", name contient "12 Rue ..." : on retire le numéro.
  if (
    properties.housenumber &&
    street.toLowerCase().startsWith(properties.housenumber.toLowerCase())
  ) {
    street = street.slice(properties.housenumber.length).trim();
  }
  return {
    label: properties.label || "",
    streetNumber: properties.housenumber || "",
    street,
    postalCode: properties.postcode || "",
    city: properties.city || "",
  };
};

const AddressAutocompleteInput = ({ onSelect, placeholder, disabled }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(debounceRef.current);
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const search = (value) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    fetch(
      `${API_URL}?q=${encodeURIComponent(value)}&limit=6&autocomplete=1`,
      { signal: controller.signal }
    )
      .then((resp) => resp.json())
      .then((data) => {
        setSuggestions((data.features || []).map(parseFeature));
        setIsOpen(true);
        setActiveIndex(-1);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setIsLoading(false);
          setSuggestions([]);
        }
      });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(debounceRef.current);
    if (value.trim().length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => search(value.trim()), 300);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.label);
    setIsOpen(false);
    setSuggestions([]);
    onSelect(suggestion);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      // Empêche la soumission du formulaire parent tant que la liste est ouverte
      e.preventDefault();
      handleSelect(suggestions[activeIndex >= 0 ? activeIndex : 0]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <Form.Control
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        placeholder={
          placeholder ||
          "Tapez ou collez une adresse (ex : 12 rue de la Paix 75002 Paris)"
        }
        disabled={disabled}
        autoComplete="off"
      />
      {isLoading && (
        <Spinner
          animation="border"
          size="sm"
          style={{ position: "absolute", right: "10px", top: "10px" }}
        />
      )}
      {isOpen && suggestions.length > 0 && (
        <ListGroup
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1100,
            maxHeight: "240px",
            overflowY: "auto",
            boxShadow: "0 4px 8px rgba(0,0,0,.15)",
          }}
        >
          {suggestions.map((suggestion, index) => (
            <ListGroup.Item
              action
              key={`${suggestion.label}-${index}`}
              active={index === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(suggestion);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {suggestion.label}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;
